// src/views/admin/ADUtilisateurs.jsx
//
// PERMISSIONS (module "utilisateurs") — même schéma que ADClasses / ADEleves :
// - utilisateurs.creer              : bouton "Nouvel utilisateur" (+ "Créer le premier utilisateur")
// - utilisateurs.importer           : bouton "Importer Excel"
// - utilisateurs.modifier           : bouton "Modifier"
// - utilisateurs.reinitialiser_mdp  : bloc "Réinitialiser le mot de passe" dans le formulaire d'édition
// - utilisateurs.activer            : bouton Activer/Désactiver + case "Compte actif" en édition
// - utilisateurs.supprimer          : bouton "Supprimer"
// - utilisateurs.assigner_profil    : sélecteur "Profil administrateur" dans le formulaire
// - utilisateurs.gerer_profils      : onglet "Profils administrateurs" (l'onglet "Utilisateurs" reste toujours visible)
// Le bouton "Voir détails" reste visible pour tous.
// Le profil administrateur (nom) est affiché pour tous dans le tableau et le détail.

import { useState, useEffect, useRef } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { useNavigate } from "react-router-dom";
import { urlimage } from "../../utils/config";
import { usePermissions } from "../../context/PermissionsContext";
import ADProfils from "./ADProfils";

// ─── CONSTANTES ──────────────────────────────────────────────────────────────
const ROLE_LABELS = {
  ADMINISTRATEUR: { label: "Administrateur", cls: "pu" },
  ENSEIGNANT: { label: "Enseignant", cls: "bl" },
  PARENT: { label: "Parent", cls: "info" },
};

const EMPTY = {
  username: "",
  first_name: "",
  last_name: "",
  email: "",
  role: "ENSEIGNANT",
  telephone1: "",
  telephone2: "",
  is_active: true,
  password: "",
  password2: "",
  profil_administrateur: "",
};

const initials = u => `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.toUpperCase() || u.username?.slice(0, 2).toUpperCase() || "U";

// ─── HELPERS PARTAGÉS ────────────────────────────────────────────────────────
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/')) return `${urlimage}${path}`;
  return `${urlimage}/${path}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("fr-FR");
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleString("fr-FR");
  } catch {
    return dateStr;
  }
};

const getGenreLabel = (genre) => {
  if (genre === "M") return "Masculin";
  if (genre === "F") return "Féminin";
  return genre || "—";
};

// Classe la plus récente parmi les inscriptions d'un enfant
const getCurrentClasse = (eleve) => {
  if (!eleve.inscriptions || eleve.inscriptions.length === 0) return "Non inscrit";
  const sorted = [...eleve.inscriptions].sort((a, b) => {
    const dateA = new Date(a.annee_scolaire_detail?.date_debut || a.date_inscription || 0);
    const dateB = new Date(b.annee_scolaire_detail?.date_debut || b.date_inscription || 0);
    return dateB - dateA;
  });
  return sorted[0]?.classe_nom || "Non renseigné";
};

// ─── COMPOSANTS ──────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, maxWidth = 560 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const F = ({ label, type = "text", placeholder = "", required = false, value, onChange, readOnly = false }) => (
  <div className="fg">
    <label>{label} {required && <span style={{ color: "var(--rd)" }}>*</span>}</label>
    <input
      type={type}
      placeholder={placeholder}
      value={value ?? ""}
      onChange={onChange}
      readOnly={readOnly}
      style={readOnly ? { backgroundColor: "var(--s2)", cursor: "not-allowed" } : {}}
    />
  </div>
);

// ─── MODAL DÉTAIL (contenu adapté au rôle) ───────────────────────────────────
function DetailModal({ user, slug, profilLabel, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const { getParentDetail, getEnseignements } = useAdminService();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        if (user.role === "PARENT") {
          const d = await getParentDetail(slug, user.id);
          if (!cancelled) setData({ type: "PARENT", enfants: d.enfants || [] });
        } else if (user.role === "ENSEIGNANT") {
          const all = await getEnseignements(slug);
          const mine = (all || []).filter(e => e.enseignant === user.id);

          const byClasse = {};
          mine.forEach(e => {
            if (!byClasse[e.classe_nom]) byClasse[e.classe_nom] = new Set();
            if (e.matiere_nom) byClasse[e.classe_nom].add(e.matiere_nom);
          });
          const classes = Object.entries(byClasse).map(([classe_nom, matieresSet]) => ({
            classe_nom,
            matieres: Array.from(matieresSet),
          }));
          if (!cancelled) setData({ type: "ENSEIGNANT", classes });
        } else {
          if (!cancelled) setData({ type: "ADMINISTRATEUR" });
        }
      } catch (error) {
        console.error("Erreur chargement détail utilisateur:", error);
        if (!cancelled) setData({ type: "ERROR" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user.id, user.role, slug]);

  const rl = ROLE_LABELS[user.role] || ROLE_LABELS.ADMINISTRATEUR;

  return (
    <Modal title="Détail de l'utilisateur" onClose={onClose} maxWidth={640}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontSize: 17, fontWeight: 700 }}>
          {user.first_name} {user.last_name}
        </div>
        <div style={{ fontSize: 13, color: "var(--m)", marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
          @{user.username}
          <span className={`pill ${rl.cls}`}>{rl.label}</span>
          <span className={`pill ${user.is_active !== false ? "ok" : "neu"}`}>
            {user.is_active !== false ? "Actif" : "Inactif"}
          </span>
        </div>

        {/* Infos communes à TOUS les rôles */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
          <div><span style={{ color: "var(--m)" }}>Email :</span> {user.email || "—"}</div>
          <div><span style={{ color: "var(--m)" }}>Téléphone 1 :</span> {user.telephone1 || "—"}</div>
          <div><span style={{ color: "var(--m)" }}>Téléphone 2 :</span> {user.telephone2 || "—"}</div>
          <div><span style={{ color: "var(--m)" }}>Inscrit le :</span> {formatDate(user.date_joined)}</div>
          {user.role === "ADMINISTRATEUR" && (
            <div style={{ gridColumn: "1 / -1" }}>
              <span style={{ color: "var(--m)" }}>Profil administrateur :</span> {profilLabel || "—"}
            </div>
          )}
          <div style={{ gridColumn: "1 / -1" }}>
            <span style={{ color: "var(--m)" }}>Dernière connexion :</span> {formatDateTime(user.last_login)}
          </div>
        </div>

        {loading ? (
          <div style={{ fontSize: 13, color: "var(--h)", padding: "12px 0" }}>Chargement...</div>
        ) : (
          <>
            {/* ── PARENT : liste des enfants ─────────────────────────── */}
            {data?.type === "PARENT" && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--t)", marginBottom: 8 }}>
                  Enfants ({(data.enfants || []).length})
                </div>
                {data.enfants && data.enfants.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {data.enfants.map(e => (
                      <div key={e.id} style={{
                        display: "flex", gap: 12,
                        padding: "10px 12px", background: "var(--s2)", borderRadius: 8,
                        border: "1px solid var(--b)",
                      }}>
                        {e.photo ? (
                          <img
                            src={getImageUrl(e.photo)}
                            alt={`${e.prenom} ${e.nom}`}
                            style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                          />
                        ) : (
                          <div style={{
                            width: 52, height: 52, borderRadius: "50%", flexShrink: 0,
                            background: "var(--b)", display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 20, color: "var(--h)"
                          }}>
                            <i className="ti ti-user" />
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{e.prenom} {e.nom}</div>
                            <span style={{
                              fontFamily: "'Space Grotesk',sans-serif",
                              fontWeight: 700, fontSize: 11,
                              background: "var(--bl-bg)", color: "var(--bl)",
                              padding: "2px 7px", borderRadius: 5, flexShrink: 0,
                            }}>
                              {e.matricule}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: "var(--m)", marginTop: 3, display: "flex", flexWrap: "wrap", gap: "4px 10px" }}>
                            <span>{getGenreLabel(e.genre)}</span>
                            <span>·</span>
                            <span>{formatDate(e.date_naissance)}</span>
                            <span>·</span>
                            <span>Classe : {getCurrentClasse(e)}</span>
                          </div>
                          <div style={{ marginTop: 6 }}>
                            {e.abonnement_actif ? (
                              <div style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                <span className={`pill ${e.abonnement_actif.est_valide ? "ok" : "pu"}`}>
                                  {e.abonnement_actif.est_valide ? "Abonnement actif" : "Abonnement expiré"}
                                </span>
                                <span style={{ color: "var(--m)" }}>
                                  {e.abonnement_actif.offre_nom} — jusqu'au {formatDate(e.abonnement_actif.date_fin)}
                                </span>
                              </div>
                            ) : (
                              <span style={{ fontSize: 12, color: "var(--h)" }}>Aucun abonnement actif</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: "var(--h)" }}>Aucun enfant associé à ce parent</div>
                )}
              </div>
            )}

            {/* ── ENSEIGNANT : classes + matières enseignées ─────────── */}
            {data?.type === "ENSEIGNANT" && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--t)", marginBottom: 8 }}>
                  Classes & matières ({(data.classes || []).length})
                </div>
                {data.classes && data.classes.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {data.classes.map(c => (
                      <div key={c.classe_nom} style={{
                        padding: "10px 12px", background: "var(--s2)", borderRadius: 8,
                        border: "1px solid var(--b)",
                      }}>
                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{c.classe_nom}</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                          {c.matieres.map(m => (
                            <span key={m} className="pill bl" style={{ fontSize: 11 }}>{m}</span>
                          ))}
                          {c.matieres.length === 0 && (
                            <span style={{ fontSize: 12, color: "var(--h)" }}>Aucune matière assignée</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: "var(--h)" }}>Aucune classe assignée pour l'année active</div>
                )}
              </div>
            )}

            {/* ── ADMINISTRATEUR : rien de plus, tout est dans le bloc commun ── */}

            {data?.type === "ERROR" && (
              <div style={{ fontSize: 13, color: "var(--rd)" }}>
                Impossible de charger les informations complémentaires.
              </div>
            )}
          </>
        )}
      </div>
      <div className="factions">
        <button className="bsec" onClick={onClose}>Fermer</button>
      </div>
    </Modal>
  );
}

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────
export default function ADUtilisateurs({ slug }) {
  const navigate = useNavigate();
  const { canSee } = usePermissions();

  // Raccourcis de permissions pour garder le JSX lisible
  const peutCreer          = canSee("utilisateurs.creer");
  const peutImporter       = canSee("utilisateurs.importer");
  const peutModifier       = canSee("utilisateurs.modifier");
  const peutReinitMdp      = canSee("utilisateurs.reinitialiser_mdp");
  const peutActiver        = canSee("utilisateurs.activer");
  const peutSupprimer      = canSee("utilisateurs.supprimer");
  const peutAssignerProfil = canSee("utilisateurs.assigner_profil");
  const peutGererProfils   = canSee("utilisateurs.gerer_profils");

  const [tab, setTab] = useState("utilisateurs");
  const [users, setUsers] = useState([]);
  const [profils, setProfils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilt, setRoleFilt] = useState("tous");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
  const [currentPage, setCurrentPage] = useState(1);

  const toast = useToastHook();
  const {
    getUtilisateurs, createUtilisateur, updateUtilisateur, deleteUtilisateur,
    resetPasswordUtilisateur, getProfilsAdministrateurs,
  } = useAdminService();

  const isFirstRun = useRef(true);
  const searchTimeoutRef = useRef(null);

  // Libellé du profil d'un administrateur : le backend peut fournir
  // profil_administrateur_nom ; sinon on le retrouve dans la liste des profils.
  const getProfilLabel = (u) => {
    if (!u || u.role !== "ADMINISTRATEUR") return null;
    if (!u.profil_administrateur) return "Accès total";
    return (
      u.profil_administrateur_nom ||
      profils.find(p => String(p.id) === String(u.profil_administrateur))?.nom ||
      "Profil assigné"
    );
  };

  // ─── CHARGEMENT ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (slug) {
      loadData(1, true);
    }
  }, [slug]);

  // Liste des profils administrateurs : sert à la fois au <select> du
  // formulaire (rôle ADMINISTRATEUR) et à l'affichage du nom du profil dans
  // le tableau / le détail. Chargée indépendamment de loadData pour ne pas
  // dépendre de la recherche/pagination.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    getProfilsAdministrateurs(slug)
      .then(data => { if (!cancelled) setProfils(data); })
      .catch(() => { /* silencieux : on retombera sur le libellé de repli */ });
    return () => { cancelled = true; };
  }, [slug]);

  const loadData = async (page = 1, isInitial = false) => {
    if (isInitial) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const params = {
        search,
        role: roleFilt !== "tous" ? roleFilt : "",
        page,
        page_size: 20,
      };
      const data = await getUtilisateurs(slug, params);

      if (data.results) {
        setUsers(data.results);
        setPagination({ count: data.count, next: data.next, previous: data.previous });
      } else {
        setUsers(data);
        setPagination({ count: data.length, next: null, previous: null });
      }
      setCurrentPage(page);
    } catch (error) {
      toast.error("Impossible de charger les utilisateurs");
      console.error("Erreur loadData:", error);
    } finally {
      if (isInitial) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  };

  // Recherche et filtre avec debounce
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadData(1, false);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search, roleFilt]);

  // ─── CRUD ──────────────────────────────────────────────────────────────────
  const openCreate = () => {
    setForm({ ...EMPTY, is_active: true });
    setModal("create");
  };

  const openEdit = (u) => {
    setSelected(u);
    setForm({
      username: u.username,
      first_name: u.first_name || "",
      last_name: u.last_name || "",
      email: u.email || "",
      role: u.role,
      telephone1: u.telephone1 || "",
      telephone2: u.telephone2 || "",
      is_active: u.is_active !== undefined ? u.is_active : true,
      password: "",
      password2: "",
      profil_administrateur: u.profil_administrateur || "",
    });
    setModal("edit");
  };

  const openDelete = (u) => {
    setSelected(u);
    setModal("delete");
  };

  const openDetail = (u) => {
    setSelected(u);
    setModal("detail");
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.username.trim()) { toast.error("Le nom d'utilisateur est requis"); return; }
    if (!form.first_name.trim()) { toast.error("Le prénom est requis"); return; }
    if (!form.last_name.trim()) { toast.error("Le nom est requis"); return; }
    if (!form.email.trim()) { toast.error("L'email est requis"); return; }

    // En modification, le mot de passe est optionnel : soit les deux champs
    // sont vides (pas de changement), soit les deux sont remplis et
    // identiques (changement demandé). Sans la permission
    // utilisateurs.reinitialiser_mdp, ces champs sont ignorés.
    const mdpDemande = modal === "edit" && peutReinitMdp && !!form.password;
    if (modal === "edit" && peutReinitMdp && (form.password || form.password2)) {
      if (form.password !== form.password2) {
        toast.error("Les mots de passe ne correspondent pas");
        return;
      }
      if (form.password.length < 4) {
        toast.error("Le mot de passe est trop court");
        return;
      }
    }

    setSaving(true);
    try {
      const dataToSend = {
        username: form.username.trim().toLowerCase(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        telephone1: form.telephone1 || "",
        telephone2: form.telephone2 || "",
      };

      // is_active : à la création on l'envoie toujours ; en édition seulement
      // si l'utilisateur a la permission d'activer/désactiver un compte.
      if (modal === "create" || peutActiver) {
        dataToSend.is_active = form.is_active;
      }

      // Le profil administrateur n'a de sens que pour les comptes
      // ADMINISTRATEUR ; sinon on force null pour ne pas laisser traîner
      // une valeur héritée d'un rôle précédemment sélectionné dans le form.
      // ⚠️ Sans la permission utilisateurs.assigner_profil, on N'ENVOIE PAS
      // le champ : sinon on écraserait à null le profil existant (le select
      // est masqué, donc form.profil_administrateur n'est pas fiable).
      if (peutAssignerProfil) {
        dataToSend.profil_administrateur = form.role === "ADMINISTRATEUR" && form.profil_administrateur
          ? form.profil_administrateur
          : null;
      }

      if (modal === "create") {
        if (!form.password) {
          toast.error("Le mot de passe est requis");
          setSaving(false);
          return;
        }
        if (form.password !== form.password2) {
          toast.error("Les mots de passe ne correspondent pas");
          setSaving(false);
          return;
        }
        dataToSend.password = form.password;
        dataToSend.password2 = form.password2;

        const response = await createUtilisateur(slug, dataToSend);
        const newUser = response.user || response;
        toast.success(`Utilisateur "${newUser.username}" créé avec succès`);
        setModal(null);
        await loadData(1, false);
      } else {
        const response = await updateUtilisateur(slug, selected.id, dataToSend);
        const updatedUser = response.utilisateur || response;

        // Le champ password n'est pas géré par l'endpoint update/ (à raison,
        // cf. UtilisateurSerializer côté backend) — on passe donc par
        // l'endpoint dédié reset-password/ seulement si l'admin a rempli
        // les champs mot de passe dans le formulaire (et en a le droit).
        if (mdpDemande) {
          await resetPasswordUtilisateur(slug, selected.id, form.password);
        }

        setUsers(prev => prev.map(u => u.id === selected.id ? updatedUser : u));
        toast.success(
          mdpDemande
            ? `Utilisateur "${updatedUser.username}" modifié et mot de passe réinitialisé`
            : `Utilisateur "${updatedUser.username}" modifié avec succès`
        );
        setModal(null);
      }
    } catch (error) {
      console.error("Erreur handleSave:", error);
      let errorMessage = "Une erreur est survenue";
      if (error.response?.data) {
        if (typeof error.response.data === 'object') {
          const errors = Object.values(error.response.data).flat();
          errorMessage = errors.join("\n");
        } else {
          errorMessage = error.response.data.error || error.response.data.detail || errorMessage;
        }
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteUtilisateur(slug, selected.id);
      toast.success(`Utilisateur "${selected.username}" supprimé avec succès`);
      setModal(null);
      await loadData(currentPage, false);
    } catch (error) {
      console.error("Erreur handleDelete:", error);
      let errorMessage = "Impossible de supprimer cet utilisateur";
      if (error.response?.data) {
        errorMessage = error.response.data.error || error.response.data.detail || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id, currentActive) => {
    setRefreshing(true);
    try {
      const response = await updateUtilisateur(slug, id, { is_active: !currentActive });
      const updatedUser = response.utilisateur || response;
      setUsers(prev => prev.map(u => u.id === id ? updatedUser : u));
      toast.success(`Compte ${!currentActive ? "activé" : "désactivé"} avec succès`);
    } catch (error) {
      console.error("Erreur toggleActive:", error);
      toast.error("Impossible de modifier le statut");
    } finally {
      setRefreshing(false);
    }
  };

  const avColor = (role) => ROLE_LABELS[role]?.cls || "bl";

  // ─── RENDU ──────────────────────────────────────────────────────────────────
  if (loading) return <Loader />;

  return (
    <div>
      {/* Onglets — "Utilisateurs" toujours visible ; "Profils administrateurs"
          seulement avec utilisateurs.gerer_profils.
          Thème bleu + or, identique aux onglets de ADMatieres.jsx. */}
      <div className="tab-nav">
        <button
          className={`tab-btn ${tab === "utilisateurs" ? "active" : ""}`}
          onClick={() => setTab("utilisateurs")}
        >
          <i className="ti ti-users" /> <span>UTILISATEURS</span>
        </button>
        {peutGererProfils && (
          <button
            className={`tab-btn ${tab === "profils" ? "active" : ""}`}
            onClick={() => setTab("profils")}
          >
            <i className="ti ti-shield-lock" /> <span>PROFILS ADMINISTRATEURS</span>
          </button>
        )}
      </div>


      {tab === "profils" && peutGererProfils ? <ADProfils slug={slug} /> : (
        <>
          {/* Barre d'outils */}
          <div className="sb2">
            {peutCreer && (
              <button className="bprim" onClick={openCreate}>
                <i className="ti ti-user-plus" /> Nouvel utilisateur
              </button>
            )}
            {/* {peutImporter && (
              <button
                className="bprim"
                onClick={() => navigate(`/${slug}/admin/import-utilisateurs`)}
                style={{ background: "#28a745" }}
                onMouseEnter={e => e.currentTarget.style.background = "#1e7e34"}
                onMouseLeave={e => e.currentTarget.style.background = "#28a745"}
              >
                <i className="ti ti-file-spreadsheet" /> Importer Excel
              </button>
            )} */}
            <input
              type="text"
              placeholder="Rechercher un utilisateur…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ minWidth: 200 }}
            />
            <select value={roleFilt} onChange={e => setRoleFilt(e.target.value)}>
              <option value="tous">Tous les rôles</option>
              {Object.entries(ROLE_LABELS).map(([r, { label }]) => (
                <option key={r} value={r}>{label}</option>
              ))}
            </select>
            <button className="bsec" onClick={() => loadData(1, false)} disabled={refreshing}>
              <i className={`ti ${refreshing ? "ti-loader" : "ti-refresh"}`}
                 style={refreshing ? { animation: "spin 1s linear infinite" } : {}} />
              Actualiser
            </button>
          </div>

          <div style={{ fontSize: 13, color: "var(--m)", marginBottom: 8 }}>
            {pagination.count} utilisateur(s) trouvé(s)
            {refreshing && <span style={{ marginLeft: 8 }}>
              <i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Chargement...
            </span>}
          </div>

          {/* Tableau — la colonne Rôle est un peu plus large pour afficher
              le nom du profil sous le badge de rôle (administrateurs). */}
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table className="tbl" style={{ width: "100%", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={{ width: "20%" }}>Utilisateur</th>
                  <th style={{ width: "18%" }}>Email</th>
                  <th style={{ width: "16%" }}>Rôle</th>
                  <th style={{ width: "14%" }}>Téléphone</th>
                  <th style={{ width: "10%" }}>Statut</th>
                  <th style={{ width: "22%" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const rl = ROLE_LABELS[u.role] || ROLE_LABELS.ADMINISTRATEUR;
                  const isActive = u.is_active !== false;
                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="avc">
                          <div className={`av ${avColor(u.role)}`}>{initials(u)}</div>
                          <div>
                            <div style={{ fontWeight: 500 }}>{u.first_name} {u.last_name}</div>
                            <div style={{ fontSize: 11, color: "var(--h)" }}>@{u.username}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ color: "var(--m)", fontSize: 13 }}>{u.email}</td>
                      <td>
                        <span className={`pill ${rl.cls}`}>{rl.label}</span>
                        {u.role === "ADMINISTRATEUR" && (
                          <div style={{ fontSize: 11, marginTop: 4, color: u.profil_administrateur ? "var(--m)" : "var(--h)" }}>
                            <i className={`ti ${u.profil_administrateur ? "ti-shield-lock" : "ti-shield-check"}`} />{" "}
                            {getProfilLabel(u)}
                          </div>
                        )}
                      </td>
                      <td style={{ color: "var(--m)", fontSize: 13 }}>{u.telephone1 || "-"}</td>
                      <td>
                        <span className={`pill ${isActive ? "ok" : "neu"}`}>
                          {isActive ? "Actif" : "Inactif"}
                        </span>
                      </td>
                      <td>
                        <div className="act">
                          {/* Voir détails : visible pour tous */}
                          <button
                            onClick={() => openDetail(u)}
                            title="Voir détails"
                            style={{ background: "#17a2b8", color: "white" }}
                            onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                            onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                          >
                            <i className="ti ti-eye" />
                          </button>

                          {/* utilisateurs.modifier */}
                          {peutModifier && (
                            <button
                              onClick={() => openEdit(u)}
                              title={peutReinitMdp ? "Modifier (inclut la réinitialisation du mot de passe)" : "Modifier"}
                              style={{ background: "#ffc107", color: "black" }}
                              onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                            >
                              <i className="ti ti-edit" />
                            </button>
                          )}

                          {/* utilisateurs.activer */}
                          {peutActiver && (
                            <button
                              onClick={() => toggleActive(u.id, isActive)}
                              title={isActive ? "Désactiver" : "Activer"}
                              style={{
                                background: isActive ? "#fd7e14" : "#28a745",
                                color: "white"
                              }}
                              onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                            >
                              <i className={`ti ${isActive ? "ti-lock" : "ti-lock-open"}`} />
                            </button>
                          )}

                          {/* utilisateurs.supprimer */}
                          {peutSupprimer && (
                            <button
                              className="del"
                              onClick={() => openDelete(u)}
                              title="Supprimer"
                            >
                              <i className="ti ti-trash" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && !refreshing && (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center", color: "var(--h)", padding: "40px" }}>
                      {search || roleFilt !== "tous" ? (
                        <>
                          <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                          Aucun utilisateur ne correspond à votre recherche
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: 32, marginBottom: 8 }}>👤</div>
                          Aucun utilisateur trouvé<br />
                          {peutCreer && (
                            <button className="bsec" style={{ marginTop: 12 }} onClick={openCreate}>
                              <i className="ti ti-user-plus" /> Créer le premier utilisateur
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                )}
                {users.length === 0 && refreshing && (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                      <i className="ti ti-loader" style={{ fontSize: 32, animation: "spin 1s linear infinite" }} />
                      <div style={{ marginTop: 8, color: "var(--m)" }}>Chargement des utilisateurs...</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.count > 20 && (
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 12,
              fontSize: 14,
              color: "var(--m)"
            }}>
              <span>
                Affichage de {users.length} sur {pagination.count} utilisateurs
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  className="bsec"
                  onClick={() => loadData(currentPage - 1, false)}
                  disabled={!pagination.previous || refreshing}
                  style={{ padding: "6px 12px" }}
                >
                  <i className="ti ti-chevron-left" /> Précédent
                </button>
                <span style={{ padding: "6px 12px" }}>
                  Page {currentPage}
                </span>
                <button
                  className="bsec"
                  onClick={() => loadData(currentPage + 1, false)}
                  disabled={!pagination.next || refreshing}
                  style={{ padding: "6px 12px" }}
                >
                  Suivant <i className="ti ti-chevron-right" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal Détail (adapté au rôle) */}
      {modal === "detail" && selected && (
        <DetailModal
          user={selected}
          slug={slug}
          profilLabel={getProfilLabel(selected)}
          onClose={() => setModal(null)}
        />
      )}

      {/* Modal Créer/Modifier — la réinitialisation du mot de passe en mode
          édition n'apparaît que avec utilisateurs.reinitialiser_mdp */}
      {((modal === "create" && peutCreer) || (modal === "edit" && peutModifier)) && (
        <Modal title={modal === "create" ? "Nouvel utilisateur" : "Modifier l'utilisateur"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fgrid">
              <F
                label="Prénom"
                placeholder="Prénom"
                required
                value={form.first_name}
                onChange={e => handleFormChange('first_name', e.target.value)}
              />
              <F
                label="Nom"
                placeholder="Nom"
                required
                value={form.last_name}
                onChange={e => handleFormChange('last_name', e.target.value)}
              />
            </div>
            <F
              label="Nom d'utilisateur"
              placeholder="ex: enseignant.ecole"
              required
              value={form.username}
              onChange={e => handleFormChange('username', e.target.value)}
            />
            <F
              label="Email"
              type="email"
              placeholder="email@ecole.bf"
              required
              value={form.email}
              onChange={e => handleFormChange('email', e.target.value)}
            />

            <div className="fg">
              <label>Rôle *</label>
              <select value={form.role} onChange={e => handleFormChange('role', e.target.value)}>
                {Object.entries(ROLE_LABELS).map(([r, { label }]) => (
                  <option key={r} value={r}>{label}</option>
                ))}
              </select>
            </div>

            {/* utilisateurs.assigner_profil */}
            {form.role === "ADMINISTRATEUR" && peutAssignerProfil && (
              <div className="fg">
                <label>Profil administrateur</label>
                <select
                  value={form.profil_administrateur}
                  onChange={e => handleFormChange('profil_administrateur', e.target.value)}
                >
                  <option value="">Accès total (aucun profil)</option>
                  {profils.map(p => (
                    <option key={p.id} value={p.id}>{p.nom}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="fgrid">
              <F
                label="Téléphone 1"
                placeholder="+226 XX XX XX XX"
                value={form.telephone1}
                onChange={e => handleFormChange('telephone1', e.target.value)}
              />
              <F
                label="Téléphone 2"
                placeholder="+226 XX XX XX XX"
                value={form.telephone2}
                onChange={e => handleFormChange('telephone2', e.target.value)}
              />
            </div>

            {modal === "create" && (
              <div className="fgrid">
                <F
                  label="Mot de passe"
                  type="password"
                  placeholder="••••••••"
                  required
                  value={form.password}
                  onChange={e => handleFormChange('password', e.target.value)}
                />
                <F
                  label="Confirmer le mot de passe"
                  type="password"
                  placeholder="••••••••"
                  required
                  value={form.password2}
                  onChange={e => handleFormChange('password2', e.target.value)}
                />
              </div>
            )}

            {/* utilisateurs.reinitialiser_mdp */}
            {modal === "edit" && peutReinitMdp && (
              <div style={{
                marginTop: 4, padding: "10px 12px",
                background: "var(--s2)", borderRadius: 8,
                border: "1px solid var(--b)",
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                  <i className="ti ti-key" /> Réinitialiser le mot de passe (optionnel)
                </div>
                <div className="fgrid">
                  <F
                    label="Nouveau mot de passe"
                    type="password"
                    placeholder="Laisser vide pour ne pas changer"
                    value={form.password}
                    onChange={e => handleFormChange('password', e.target.value)}
                  />
                  <F
                    label="Confirmer"
                    type="password"
                    placeholder="Laisser vide pour ne pas changer"
                    value={form.password2}
                    onChange={e => handleFormChange('password2', e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Compte actif : toujours à la création, en édition seulement avec utilisateurs.activer */}
            {(modal === "create" || peutActiver) && (
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <input
                  type="checkbox"
                  id="u-actif"
                  checked={!!form.is_active}
                  onChange={e => handleFormChange('is_active', e.target.checked)}
                />
                <label htmlFor="u-actif" style={{ fontSize: 13, color: "var(--m)" }}>
                  Compte actif
                </label>
              </div>
            )}

            <div className="fg">
              <label>Établissement</label>
              <input
                type="text"
                value={slug || "—"}
                readOnly
                style={{
                  backgroundColor: "var(--s2)",
                  cursor: "not-allowed",
                  padding: "9px 13px",
                  border: "0.5px solid var(--b2)",
                  borderRadius: "5px",
                  fontSize: "14px",
                  fontFamily: "inherit",
                  width: "100%",
                  color: "var(--m)"
                }}
              />
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>
              Annuler
            </button>
            <button className="bprim" onClick={handleSave} disabled={saving}>
              {saving ? (
                <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> En cours...</>
              ) : (
                <><i className="ti ti-check" /> {modal === "create" ? "Créer" : "Enregistrer"}</>
              )}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Suppression */}
      {modal === "delete" && selected && peutSupprimer && (
        <Modal title="Confirmer la suppression" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)", lineHeight: 1.6 }}>
            Êtes-vous sûr de vouloir supprimer le compte de <br />
            <strong style={{ color: "var(--t)", fontSize: 16 }}>
              {selected.first_name} {selected.last_name}
            </strong>
            <span style={{ color: "var(--h)", fontSize: 13 }}> (@{selected.username})</span>
            <div style={{ marginTop: 8, fontSize: 13, color: "var(--rd)" }}>
              ⚠️ Cette action est irréversible.
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>
              Annuler
            </button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>
              {saving ? (
                <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Suppression...</>
              ) : (
                <><i className="ti ti-trash" /> Supprimer</>
              )}
            </button>
          </div>
        </Modal>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* ─── ONGLETS (thème bleu + or, identique à ADMatieres.jsx) ─── */
        .tab-nav {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          background: linear-gradient(180deg, var(--pr-dk) 0%, var(--pr) 100%);
          border-radius: 10px;
          border-bottom: 3px solid var(--or);
          padding: 8px 12px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 18px;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          background: transparent;
          color: rgba(255,255,255,0.75);
          font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .tab-btn i { font-size: 18px; }

        .tab-btn:hover {
          background: rgba(255,255,255,0.12);
          color: var(--or-lt);
        }

        .tab-btn.active {
          background: white;
          color: var(--pr);
          box-shadow: inset 0 -3px 0 var(--or), 0 2px 8px rgba(0,0,0,0.15);
        }
        .tab-btn.active i { color: var(--or); }
        .tab-btn.active:hover { background: white; color: var(--pr); }

        @media (max-width: 768px) {
          .tab-btn { padding: 6px 10px; font-size: 11px; }
          .tab-btn i { font-size: 14px; }
        }

        @media (max-width: 576px) {
          .tab-btn span { display: none; }
          .tab-btn i { font-size: 18px; margin: 0; }
        }

        .act button {
          width: 30px;
          height: 30px;
          border-radius: 4px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all .2s;
        }

        .act button:hover {
          opacity: 0.8 !important;
        }

        .act button.del {
          background: var(--rd);
          color: white;
        }

        .act button.del:hover {
          background: var(--rd-dk);
        }
      `}</style>
    </div>
  );
}